import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bloodBankAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GROUP_COLORS = { 'A+': '#dc2626', 'A-': '#b91c1c', 'B+': '#2563eb', 'B-': '#1d4ed8', 'AB+': '#7c3aed', 'AB-': '#6d28d9', 'O+': '#059669', 'O-': '#047857' };

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

export default function BloodBankDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [requests, setRequests] = useState([]);
    const [tab, setTab] = useState('dashboard');
    const [showRequest, setShowRequest] = useState(false);
    const [reqForm, setReqForm] = useState({ patient_name: '', blood_group: 'A+', component: 'Whole Blood', units_requested: 1, priority: 'Routine', ward: '', doctor_name: '', indication: '' });
    const [loading, setLoading] = useState(false);

    const loadDashboard = async () => {
        try {
            const res = await bloodBankAPI.getDashboard();
            setData(res.data);
        } catch { toast.error('Failed to load dashboard'); }
    };

    const loadRequests = async () => {
        try {
            const res = await bloodBankAPI.getRequests({});
            setRequests(res.data);
        } catch {}
    };

    useEffect(() => { loadDashboard(); loadRequests(); }, []);

    const handleRequest = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await bloodBankAPI.createRequest(reqForm);
            toast.success('Blood request created');
            setShowRequest(false);
            setReqForm({ patient_name: '', blood_group: 'A+', component: 'Whole Blood', units_requested: 1, priority: 'Routine', ward: '', doctor_name: '', indication: '' });
            loadRequests();
            loadDashboard();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setLoading(false); }
    };

    const stockLevel = (units) => {
        if (units === 0) return { color: '#dc2626', bg: '#fef2f2', label: 'OUT' };
        if (units <= 2) return { color: '#d97706', bg: '#fffbeb', label: 'LOW' };
        return { color: '#059669', bg: '#f0fdf4', label: 'OK' };
    };

    const priorityColor = { Emergency: '#dc2626', Urgent: '#d97706', Routine: '#059669' };
    const statusColor = { Pending: '#d97706', Fulfilled: '#059669', Partial: '#2563eb', Cancelled: '#6b7280' };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>🩸 Blood Bank</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Inventory, donors & transfusion management</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => navigate('/blood-bank/donors')}
                        style={{ padding: '9px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        👤 Donors
                    </button>
                    <button onClick={() => navigate('/blood-bank/issue')}
                        style={{ padding: '9px 18px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        💉 Issue Blood
                    </button>
                    <button onClick={() => setShowRequest(true)}
                        style={{ padding: '9px 18px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        + Request
                    </button>
                </div>
            </div>

            {/* Expiring soon alert */}
            {data?.expiringSoon?.length > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span>⚠️</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                        {data.expiringSoon.reduce((s, r) => s + parseInt(r.units), 0)} bag(s) expiring within 7 days!
                        {data.expiringSoon.map(r => ` ${r.blood_group}(${r.units})`).join(',')}
                    </span>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                {[['dashboard', '📊 Stock Overview'], ['requests', '📋 Blood Requests']].map(([key, label]) => (
                    <button key={key} onClick={() => setTab(key)}
                        style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: tab === key ? '#1e3a5f' : '#f1f5f9', color: tab === key ? 'white' : '#374151' }}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'dashboard' && (
                <>
                    {/* Summary row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
                        {[
                            { label: 'Total Units Available', value: data ? Object.values(data.stock).reduce((a, b) => a + b, 0) : '—', icon: '🩸', color: '#dc2626' },
                            { label: 'Eligible Donors', value: data?.eligibleDonors ?? '—', icon: '👤', color: '#7c3aed' },
                            { label: 'Pending Requests', value: data?.requests?.find(r => r.status === 'Pending')?.count ?? 0, icon: '📋', color: '#d97706' },
                            { label: 'Fulfilled Today', value: data?.requests?.find(r => r.status === 'Fulfilled')?.count ?? 0, icon: '✅', color: '#059669' },
                        ].map(c => (
                            <div key={c.label} style={{ background: 'white', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ fontSize: 20 }}>{c.icon}</div>
                                <div>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{c.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Blood group stock - table style */}
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 20 }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>🩸 Blood Group Stock</h3>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Live Inventory</span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Blood Group', 'Units', 'Stock Level', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: '#64748b', textAlign: 'left', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {BLOOD_GROUPS.map(g => {
                                    const units = data?.stock?.[g] ?? 0;
                                    const level = stockLevel(units);
                                    const pct = Math.min(units * 10, 100);
                                    const statusText = units === 0 ? 'OUT OF STOCK' : units <= 2 ? 'LOW STOCK' : 'AVAILABLE';
                                    return (
                                        <tr key={g} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '9px 14px' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 24, borderRadius: 5, fontSize: 13, fontWeight: 900, color: 'white', background: GROUP_COLORS[g] }}>{g}</span>
                                            </td>
                                            <td style={{ padding: '9px 14px', fontSize: 18, fontWeight: 800, color: level.color }}>{units}</td>
                                            <td style={{ padding: '9px 14px' }}>
                                                <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden', width: 120 }}>
                                                    <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: level.color }} />
                                                </div>
                                            </td>
                                            <td style={{ padding: '9px 14px' }}>
                                                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: level.color, marginRight: 5 }} />
                                                <span style={{ fontSize: 11, fontWeight: 700, color: level.color }}>{statusText}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {tab === 'requests' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Request No.', 'Patient', 'Blood Group', 'Component', 'Units', 'Priority', 'Ward', 'Status', 'Date', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 ? (
                                <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No blood requests yet</td></tr>
                            ) : requests.map(r => (
                                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: '#1e3a5f' }}>{r.request_number}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{r.patient_name || '—'}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ fontWeight: 800, fontSize: 14, color: GROUP_COLORS[r.blood_group] }}>{r.blood_group}</span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{r.component}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 700 }}>{r.units_issued}/{r.units_requested}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ color: priorityColor[r.priority] || '#374151', fontWeight: 700, fontSize: 12 }}>{r.priority}</span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{r.ward || '—'}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ background: `${statusColor[r.status]}20`, color: statusColor[r.status], borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{r.status}</span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b' }}>{new Date(r.requested_at).toLocaleDateString('en-PK')}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        {r.status === 'Pending' && (
                                            <button onClick={() => navigate('/blood-bank/issue', { state: { request: r } })}
                                                style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                Issue
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Blood Request Modal */}
            {showRequest && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 500, maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#dc2626', margin: 0 }}>🩸 Blood Request</h3>
                            <button onClick={() => setShowRequest(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
                        </div>
                        <form onSubmit={handleRequest}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Patient Name *</label>
                                    <input required style={inputStyle} value={reqForm.patient_name} onChange={e => setReqForm(f => ({ ...f, patient_name: e.target.value }))} placeholder="Patient name" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Blood Group *</label>
                                    <select required style={{ ...inputStyle, background: 'white' }} value={reqForm.blood_group} onChange={e => setReqForm(f => ({ ...f, blood_group: e.target.value }))}>
                                        {BLOOD_GROUPS.map(g => <option key={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Component</label>
                                    <select style={{ ...inputStyle, background: 'white' }} value={reqForm.component} onChange={e => setReqForm(f => ({ ...f, component: e.target.value }))}>
                                        {['Whole Blood', 'Packed RBCs', 'Platelets', 'FFP', 'Cryoprecipitate'].map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Units *</label>
                                    <input required type="number" min="1" style={inputStyle} value={reqForm.units_requested} onChange={e => setReqForm(f => ({ ...f, units_requested: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Priority</label>
                                    <select style={{ ...inputStyle, background: 'white' }} value={reqForm.priority} onChange={e => setReqForm(f => ({ ...f, priority: e.target.value }))}>
                                        {['Emergency', 'Urgent', 'Routine'].map(p => <option key={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Ward</label>
                                    <input style={inputStyle} value={reqForm.ward} onChange={e => setReqForm(f => ({ ...f, ward: e.target.value }))} placeholder="Ward / OT / ICU" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Doctor Name</label>
                                    <input style={inputStyle} value={reqForm.doctor_name} onChange={e => setReqForm(f => ({ ...f, doctor_name: e.target.value }))} placeholder="Requesting doctor" />
                                </div>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Indication</label>
                                    <input style={inputStyle} value={reqForm.indication} onChange={e => setReqForm(f => ({ ...f, indication: e.target.value }))} placeholder="e.g. Surgery, Anemia, Trauma" />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button type="submit" disabled={loading}
                                    style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {loading ? 'Submitting...' : '🩸 Submit Request'}
                                </button>
                                <button type="button" onClick={() => setShowRequest(false)}
                                    style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
